import React, { useRef } from 'react';

import './PerfVsIndexCapsuleChart.scss';
import { XAxisContextProvider } from 'utils/components/TimeSeriesChart/XAxisContext';
import { scaleLinear } from 'd3-scale';
import { styleConstants as tileStyleConstants } from 'widgets/tiles/components/styles/variables.module.constants';
import { TileTimeSeriesChart } from 'widgets/tiles/components/tile/TileTimeSeriesChart';
import { extentLinear } from '@d3fc/d3fc-extent';
import {
    deriveRelativePerfIndex,
    deriveRelativePerfOrderToIndex,
    PerfVsIndexData,
} from 'widgets/perfVsIndexCapsule/perfVsIndexCapsuleData';
import { perfVsIndexCapsuleSeries } from 'widgets/perfVsIndexCapsule/components/chart/series/perfVsIndexCapsuleSeries';
import { splitPosNeg } from 'utils/components/TimeSeriesChart/posNegGradientLine';
import { withOppositeAccessor } from 'utils/d3fc/accessors';
import { PerfVsIndexCapsuleChartHeader } from 'widgets/perfVsIndexCapsule/components/chart/PerfVsIndexCapsuleChartHeader';
import classNames from 'classnames';

export interface PerfVsIndexCapsuleChartProps {
    chartKey: string;
    showIndexPerf: boolean;
    data: PerfVsIndexData;
    onClick: (time: number | undefined) => void;
}

export function PerfVsIndexCapsuleChart(props: PerfVsIndexCapsuleChartProps) {
    const {
        data: { timeSeries, startTime, endTime, breaksInTrading },
        onClick,
        chartKey,
        showIndexPerf,
    } = props;

    const height =
        tileStyleConstants.tileHeight -
        tileStyleConstants.tileWidgetToolbarHeight -
        tileStyleConstants.tileWidgetToolbarMarginTop -
        14;

    const undefinedAccessor = () => undefined;

    const yExtent = extentLinear()
        .accessors([
            ...(showIndexPerf
                ? withOppositeAccessor(deriveRelativePerfIndex)
                : [undefinedAccessor]),
            ...withOppositeAccessor(deriveRelativePerfOrderToIndex),
        ])
        .pad([0.1, 0.1]);
    const yScaleRef = useRef(scaleLinear());
    const yDomain = yExtent(timeSeries);
    yScaleRef.current.domain(yDomain);

    const dataToBind = splitPosNeg(
        timeSeries,
        'relativePerfOrderToIndex',
        'relativePerfIndex'
    );

    const cssClasses = classNames(
        'PerfVsIndexCapsuleChart flippable',
        !showIndexPerf ? 'hide-index-perf' : undefined
    );

    return (
        <div className={cssClasses}>
            <XAxisContextProvider
                timeSeriesChartWidth={tileStyleConstants.tileWidth}
                startTime={startTime}
                endTime={endTime}
                breaks={breaksInTrading}
                rangeLabels={[]}
                timeLineLabels={[]}
            >
                <PerfVsIndexCapsuleChartHeader data={props.data} />
                <TileTimeSeriesChart
                    id={`PerfVsIndexCapsuleChart-${chartKey}`}
                    height={height}
                    data={dataToBind}
                    series={perfVsIndexCapsuleSeries}
                    displayXAxis={true}
                    yScale={yScaleRef.current}
                    yTicks={0}
                    onChartClick={onClick}
                />
            </XAxisContextProvider>
        </div>
    );
}
